import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Pencil, Users } from 'lucide-react';
import { api } from '../lib/api';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { EmptyState } from '../components/ui/empty-state';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { PageHeader } from '@/components/layout/page-header';

type StatusFilter = 'all' | 'invited' | 'active' | 'disabled';

const ROLE_BADGE: Record<string, 'default' | 'warning' | 'success' | 'destructive'> = {
  admin: 'destructive',
  sbu: 'warning',
  finance: 'success',
  viewer: 'default',
};

const STATUS_BADGE: Record<string, 'default' | 'warning' | 'success' | 'destructive'> = {
  invited: 'warning',
  active: 'success',
  disabled: 'destructive',
};

export function AdminUsersPage() {
  const [status, setStatus] = useState<StatusFilter>('all');
  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', status],
    queryFn: () => api.listUsers(status === 'all' ? {} : { status }),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Users"
        actions={
          <Link
            to="/admin/users/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <Plus size={14} strokeWidth={1.75} />
            Add user
          </Link>
        }
      />

      <div className="flex items-center gap-2">
        <Label htmlFor="status-filter">Status:</Label>
        <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
          <SelectTrigger id="status-filter" className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="invited">Invited</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && <Skeleton className="h-40 w-full" />}
      {data && data.rows.length === 0 && (
        <EmptyState
          icon={Users}
          title="Chưa có user nào"
          description="Nhấn Add user để tạo tài khoản đầu tiên."
        />
      )}
      {data && data.rows.length > 0 && (
        <>
          {/* Desktop */}
          <div className="hidden overflow-x-auto rounded-lg border border-border bg-card md:block">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 border-b border-border bg-background text-left">
                <tr>
                  <th className="p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Email
                  </th>
                  <th className="p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Name
                  </th>
                  <th className="p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Role
                  </th>
                  <th className="p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Status
                  </th>
                  <th className="p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Departments
                  </th>
                  <th className="p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Last login
                  </th>
                  <th className="p-3"> </th>
                </tr>
              </thead>
              <tbody className="[&_tr:nth-child(even)]:bg-background">
                {data.rows.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-border last:border-0 hover:bg-background"
                  >
                    <td className="p-3 font-mono text-muted-foreground">{u.email}</td>
                    <td className="p-3 text-foreground">{u.name}</td>
                    <td className="p-3">
                      <Badge variant={ROLE_BADGE[u.role] ?? 'default'}>{u.role}</Badge>
                    </td>
                    <td className="p-3">
                      <Badge variant={STATUS_BADGE[u.status] ?? 'default'}>{u.status}</Badge>
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {u.departments.length === 0
                        ? '—'
                        : u.departments.map((d) => d.code).join(', ')}
                    </td>
                    <td className="p-3 text-muted-foreground tabular-nums">
                      {u.last_login_at ? u.last_login_at.slice(0, 10) : '—'}
                    </td>
                    <td className="p-3">
                      <Link
                        to={`/admin/users/${u.id}/edit`}
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        <Pencil size={14} />
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-2 md:hidden">
            {data.rows.map((u) => (
              <div key={u.id} className="rounded-lg border border-border bg-card p-3 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-mono text-muted-foreground">{u.email}</div>
                    <div className="font-medium text-foreground">{u.name}</div>
                  </div>
                  <Link
                    to={`/admin/users/${u.id}/edit`}
                    className="text-xs text-primary hover:underline"
                  >
                    Edit
                  </Link>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <Badge variant={ROLE_BADGE[u.role] ?? 'default'}>{u.role}</Badge>
                  <Badge variant={STATUS_BADGE[u.status] ?? 'default'}>{u.status}</Badge>
                </div>
                {u.departments.length > 0 && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    {u.departments.map((d) => d.code).join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
